import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import {
  FaCalendarAlt,
  FaCalendarWeek,
  FaClock,
  FaCreditCard,
  FaFileExcel,
  FaFilePdf,
  FaFilter,
  FaList,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaSun,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdminPageHeader from './AdminPageHeader';
import Navigation from './Navigation';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './MembershipPayments.css';

const paymentsTableStyles = {
  table: {
    style: {
      minWidth: '1400px',
    },
  },
  tableWrapper: {
    style: {
      display: 'block',
      width: 'max-content',
      minWidth: '100%',
    },
  },
  headCells: {
    style: {
      paddingLeft: '0.95rem',
      paddingRight: '0.95rem',
      paddingTop: '0.85rem',
      paddingBottom: '0.85rem',
      fontSize: '0.68rem',
      fontWeight: 500,
      letterSpacing: '0.07em',
    },
  },
  rows: {
    style: {
      minHeight: '68px',
    },
  },
  cells: {
    style: {
      paddingLeft: '0.95rem',
      paddingRight: '0.95rem',
      paddingTop: '0.85rem',
      paddingBottom: '0.85rem',
      fontSize: '0.82rem',
      lineHeight: '1.35',
    },
  },
  pagination: {
    style: {
      fontSize: '0.75rem',
    },
  },
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const currencyFormatter = new Intl.NumberFormat('id-ID');
const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const timeFormatter = new Intl.DateTimeFormat('id-ID', {
  hour: '2-digit',
  minute: '2-digit',
});

const formatActorTypeLabel = value => {
  switch ((value || '').toLowerCase()) {
    case 'karyawan':
      return 'Employee';
    case 'outlet':
      return 'Outlet';
    case 'member':
      return 'Member';
    case 'admin':
      return 'Admin';
    default:
      return 'System';
  }
};

const getPaymentStatusConfig = status => {
  switch (Number(status || 0)) {
    case 1:
      return { label: 'Completed', className: 'completed' };
    case 2:
      return { label: 'Failed', className: 'failed' };
    case 3:
      return { label: 'Cancelled', className: 'cancelled' };
    default:
      return { label: 'Pending', className: 'pending' };
  }
};

const emptyOverview = {
  today: {
    success: 0,
    pending: 0,
    failed: 0,
    qris: 0,
    cash: 0,
    transfer: 0,
  },
  this_week: {
    success: 0,
    pending: 0,
    failed: 0,
    qris: 0,
    cash: 0,
    transfer: 0,
  },
  this_month: {
    success: 0,
    pending: 0,
    failed: 0,
    qris: 0,
    cash: 0,
    transfer: 0,
  },
};

const defaultFilters = {
  paymentMethod: '',
  status: '',
  search: '',
  dateFrom: '',
  dateTo: '',
};

function buildRangeFromPreset(preset) {
  const now = new Date();
  const format = value => value.toISOString().split('T')[0];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      return { dateFrom: format(today), dateTo: format(today) };
    case 'this-week': {
      const start = new Date(today);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      return { dateFrom: format(start), dateTo: format(today) };
    }
    case 'this-month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { dateFrom: format(start), dateTo: format(today) };
    }
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

function MembershipPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(emptyOverview);
  const [methodFilter, setMethodFilter] = useState(defaultFilters.paymentMethod);
  const [statusFilter, setStatusFilter] = useState(defaultFilters.status);
  const [searchTerm, setSearchTerm] = useState(defaultFilters.search);
  const [dateFrom, setDateFrom] = useState(defaultFilters.dateFrom);
  const [dateTo, setDateTo] = useState(defaultFilters.dateTo);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [quickRange, setQuickRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const formatCurrency = value => `Rp ${currencyFormatter.format(Number(value || 0))}`;
  const formatCount = value => currencyFormatter.format(Number(value || 0));
  const formatDate = value => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? dateFormatter.format(date) : '-';
  };
  const formatTime = value => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? timeFormatter.format(date) : '-';
  };
  const formatProcessedBy = payment => {
    const actorTypeLabel = formatActorTypeLabel(payment.processed_by_actor_type);
    const actorName = (payment.processed_by_name || '').trim();

    if (actorName) {
      return `${actorName} • ${actorTypeLabel}`;
    }

    return actorTypeLabel;
  };

  const fetchPayments = useCallback(async (page = currentPage, perPage = rowsPerPage, filters = appliedFilters) => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      setLoading(false);
      navigate('/loginadmin');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}listmembershippayments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          limit: perPage,
          offset: (page - 1) * perPage,
          payment_method: filters.paymentMethod,
          status: filters.status === '' ? null : Number(filters.status),
          search: filters.search,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
        }),
      });

      const data = await response.json();
      if (data.error === 0 && Array.isArray(data.results)) {
        setPayments(data.results);
        setTotalRows(Number(data.total || 0));
        setSummary(data.summary || emptyOverview);
        return;
      }

      setPayments([]);
      setTotalRows(0);
      setSummary(emptyOverview);
      Swal.fire('Error', data.message || 'Failed to fetch payments', 'error');
    } catch (error) {
      console.error('Fetch error:', error);
      setPayments([]);
      setTotalRows(0);
      setSummary(emptyOverview);
      Swal.fire('Error', 'Failed to fetch payments', 'error');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, navigate, rowsPerPage]);

  useEffect(() => {
    fetchPayments(currentPage, rowsPerPage, appliedFilters);
  }, [appliedFilters, currentPage, fetchPayments, rowsPerPage]);

  const handleStatusChange = async (id, currentStatus, newStatus) => {
    if (Number(currentStatus) === 3) {
      Swal.fire('Info', 'Transaksi yang sudah dibatalkan tidak bisa diubah lagi.', 'info');
      return;
    }
    if (
      Number(currentStatus) === 1 &&
      Number(newStatus) !== 1 &&
      Number(newStatus) !== 3
    ) {
      Swal.fire('Info', 'Completed payments hanya bisa di-void.', 'info');
      return;
    }
    if (Number(currentStatus) === Number(newStatus)) {
      return;
    }

    let cancelReason = '';
    if (Number(newStatus) === 3) {
      const isVoidFromCompleted = Number(currentStatus) === 1;
      const confirm = await Swal.fire({
        title: isVoidFromCompleted ? 'Void transaksi membership ini?' : 'Batalkan transaksi membership ini?',
        text: isVoidFromCompleted
          ? 'Jurnal transaksi akan dibalik dan membership ini tidak lagi dianggap aktif.'
          : 'Transaksi akan ditandai cancelled dan tidak bisa diproses lagi.',
        icon: 'warning',
        showCancelButton: true,
        input: 'text',
        inputPlaceholder: isVoidFromCompleted ? 'Alasan void' : 'Alasan pembatalan',
        confirmButtonText: isVoidFromCompleted ? 'Void' : 'Batalkan',
      });
      if (!confirm.isConfirmed) {
        return;
      }
      cancelReason = confirm.value || '';
    }

    const token = localStorage.getItem('sessionToken');
    try {
      const response = await fetch(`${API_BASE_URL}updatemembershippayment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, status: newStatus, cancel_reason: cancelReason }),
      });
      const data = await response.json();
      if (data.error === 0) {
        const isVoidFromCompleted = Number(currentStatus) === 1 && Number(newStatus) === 3;
        Swal.fire(
          'Success',
          isVoidFromCompleted
            ? 'Transaksi membership berhasil di-void dan jurnal reversal sudah dibuat.'
            : Number(newStatus) === 3
              ? 'Transaksi membership berhasil dibatalkan.'
              : 'Status transaksi membership berhasil diperbarui.',
          'success'
        );
        fetchPayments(currentPage, rowsPerPage, appliedFilters);
        return;
      }

      Swal.fire('Error', data.message || 'Gagal memperbarui status', 'error');
    } catch (error) {
      console.error('Update error:', error);
      Swal.fire('Error', 'Gagal memperbarui status', 'error');
    }
  };

  const applyFilter = () => {
    setCurrentPage(1);
    setAppliedFilters({
      paymentMethod: methodFilter,
      status: statusFilter,
      search: searchTerm.trim(),
      dateFrom,
      dateTo,
    });
  };

  const handleQuickRange = preset => {
    const nextRange = buildRangeFromPreset(preset);
    setQuickRange(preset);
    setDateFrom(nextRange.dateFrom);
    setDateTo(nextRange.dateTo);
    setCurrentPage(1);
    setAppliedFilters({
      paymentMethod: methodFilter,
      status: statusFilter,
      search: searchTerm.trim(),
      ...nextRange,
    });
  };

  const resetFilters = () => {
    setMethodFilter(defaultFilters.paymentMethod);
    setStatusFilter(defaultFilters.status);
    setSearchTerm(defaultFilters.search);
    setDateFrom(defaultFilters.dateFrom);
    setDateTo(defaultFilters.dateTo);
    setQuickRange('all');
    setCurrentPage(1);
    setAppliedFilters(defaultFilters);
  };

  const exportExcel = () => {
    const excelData = payments.map(payment => ({
      ID: payment.id,
      Outlet: payment.outlet,
      MemberID: payment.member_id,
      Member: payment.member_name,
      Membership: payment.membership_name,
      Amount: formatCurrency(payment.amount),
      Method: payment.payment_method,
      ProcessedBy: formatProcessedBy(payment),
      Created: `${formatDate(payment.created_at)} ${formatTime(payment.created_at)}`,
      Expired: formatDate(payment.expired),
      Status: getPaymentStatusConfig(payment.status).label,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Membership Payments');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'membership-payments.xlsx');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text('Membership Payments Report', 14, 10);

    autoTable(doc, {
      head: [['ID', 'Outlet', 'Member', 'Membership', 'Amount', 'Method', 'Processed By', 'Created', 'Status']],
      body: payments.map(payment => [
        payment.id,
        payment.outlet,
        payment.member_name || payment.member_id,
        payment.membership_name || '-',
        formatCurrency(payment.amount),
        payment.payment_method || '-',
        formatProcessedBy(payment),
        `${formatDate(payment.created_at)} ${formatTime(payment.created_at)}`,
        getPaymentStatusConfig(payment.status).label,
      ]),
    });

    doc.save('membership-payments.pdf');
  };

  const summaryCards = [
    {
      key: 'today',
      title: 'Hari ini',
      description: 'Transaksi hari berjalan',
      icon: FaSun,
      className: 'is-today',
      metrics: [
        { key: 'success', label: 'Sukses', value: formatCount(summary.today?.success) },
        { key: 'pending', label: 'Pending', value: formatCount(summary.today?.pending) },
        { key: 'failed', label: 'Failed', value: formatCount(summary.today?.failed) },
        { key: 'qris', label: 'QRIS', value: formatCount(summary.today?.qris) },
        { key: 'cash', label: 'Cash', value: formatCount(summary.today?.cash) },
        { key: 'transfer', label: 'Transfer', value: formatCount(summary.today?.transfer) },
      ],
    },
    {
      key: 'week',
      title: 'Minggu ini',
      description: 'Transaksi minggu aktif',
      icon: FaCalendarWeek,
      className: 'is-week',
      metrics: [
        { key: 'success', label: 'Sukses', value: formatCount(summary.this_week?.success) },
        { key: 'pending', label: 'Pending', value: formatCount(summary.this_week?.pending) },
        { key: 'failed', label: 'Failed', value: formatCount(summary.this_week?.failed) },
        { key: 'qris', label: 'QRIS', value: formatCount(summary.this_week?.qris) },
        { key: 'cash', label: 'Cash', value: formatCount(summary.this_week?.cash) },
        { key: 'transfer', label: 'Transfer', value: formatCount(summary.this_week?.transfer) },
      ],
    },
    {
      key: 'month',
      title: 'Bulan ini',
      description: 'Transaksi bulan berjalan',
      icon: FaCalendarAlt,
      className: 'is-month',
      metrics: [
        { key: 'success', label: 'Sukses', value: formatCount(summary.this_month?.success) },
        { key: 'pending', label: 'Pending', value: formatCount(summary.this_month?.pending) },
        { key: 'failed', label: 'Failed', value: formatCount(summary.this_month?.failed) },
        { key: 'qris', label: 'QRIS', value: formatCount(summary.this_month?.qris) },
        { key: 'cash', label: 'Cash', value: formatCount(summary.this_month?.cash) },
        { key: 'transfer', label: 'Transfer', value: formatCount(summary.this_month?.transfer) },
      ],
    },
  ];

  const activeRangeLabel = (() => {
    if (!appliedFilters.dateFrom && !appliedFilters.dateTo) {
      return 'Semua periode';
    }

    if (appliedFilters.dateFrom && appliedFilters.dateTo) {
      return `${formatDate(appliedFilters.dateFrom)} s/d ${formatDate(appliedFilters.dateTo)}`;
    }

    return formatDate(appliedFilters.dateFrom || appliedFilters.dateTo);
  })();

  const startRecord = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
      width: '96px',
      cell: row => <span className="payments-id-chip">#{row.id}</span>,
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '180px',
      cell: row => {
        const statusConfig = getPaymentStatusConfig(row.status);
        return (
        <Form.Select
          className={`payments-status-select payments-status-select--${statusConfig.className}`}
          value={row.status}
          onChange={event => handleStatusChange(row.id, row.status, parseInt(event.target.value, 10))}
          disabled={Number(row.status) === 3}
        >
          {Number(row.status) === 1 ? (
            <>
              <option value={1}>Completed</option>
              <option value={3}>Void</option>
            </>
          ) : (
            <>
              <option value={0}>Pending</option>
              <option value={1}>Completed</option>
              <option value={2}>Failed</option>
              <option value={3}>Cancelled</option>
            </>
          )}
        </Form.Select>
        );
      },
    },
    {
      name: 'Outlet / Member',
      sortable: true,
      width: '280px',
      cell: row => (
        <div className="table-identity payments-table-identity">
          <span className="table-identity__avatar">
            {String(row.outlet || 'PF').slice(0, 2).toUpperCase()}
          </span>
          <span className="table-identity__meta">
            <strong>{row.member_name || 'Member'}</strong>
            <small>{row.outlet || 'Outlet tidak tersedia'}</small>
            <small>Member ID: {row.member_id}</small>
            <small className="payments-processed-by">Handled by {formatProcessedBy(row)}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Membership',
      selector: row => row.membership_name,
      sortable: true,
      width: '220px',
      cell: row => (
        <span className="payments-cell-stack">
          <strong>{row.membership_name || 'Membership tidak tersedia'}</strong>
          <small>Paket membership</small>
        </span>
      ),
    },
    {
      name: 'Payment Method',
      selector: row => row.payment_method,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="payments-method-pill">
          {row.payment_method || 'Tidak tersedia'}
        </span>
      ),
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="payments-cell-stack">
          <strong>{formatCurrency(row.amount)}</strong>
          <small>{row.payment_method || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Created',
      selector: row => row.created_at,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="payments-cell-stack">
          <strong>{formatDate(row.created_at)}</strong>
          <small>{formatTime(row.created_at)}</small>
        </span>
      ),
    },
    {
      name: 'Expired',
      selector: row => row.expired,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="payments-cell-stack">
          <strong>{formatDate(row.expired)}</strong>
          <small>Masa aktif</small>
        </span>
      ),
    },
  ];

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaCreditCard}
          eyebrow="Payment monitoring"
          title="Client Plan Payments"
          description="Kelola transaksi pembayaran plan dengan pagination server-side, filter yang terstruktur, dan ringkasan operasional yang tetap responsif untuk volume data besar."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Client Plan Payments' },
          ]}
          actions={(
            <Button
              variant="primary"
              onClick={() => navigate('/fitness-transactions?tab=membership')}
              className="d-flex align-items-center gap-2"
            >
              <FaPlus /> Buka Front Desk
            </Button>
          )}
          meta={(
            <>
              <span className="soft-stat">
                <FaList /> {totalRows} records
              </span>
              <span className="soft-stat">
                <FaCalendarAlt /> {activeRangeLabel}
              </span>
            </>
          )}
        />

        <section className="payments-summary-grid">
          {summaryCards.map(card => {
            const CardIcon = card.icon;
            return (
              <article key={card.key} className={`surface-card payments-summary-card ${card.className}`}>
                <div className="payments-summary-card__header">
                  <span className="payments-summary-card__icon">
                    <CardIcon />
                  </span>
                  <div className="payments-summary-card__copy">
                    <p className="payments-summary-card__eyebrow">{card.title}</p>
                    <h3>{card.title}</h3>
                    <span>{card.description}</span>
                  </div>
                </div>
                <div className="payments-summary-card__metrics">
                  {card.metrics.map(metric => (
                    <div key={`${card.key}-${metric.key}`} className="payments-summary-metric">
                      <div className="payments-summary-metric__copy">
                        <p>{metric.label}</p>
                        <strong>{metric.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </section>

        <div className="section-card table-card payments-table-card">
          <div className="payments-overview">
            <div className="payments-overview__copy">
              <span className="page-eyebrow">Overview</span>
              <h2>Payment records workspace</h2>
              <p>
                Tampilan ini menyesuaikan volume data besar dengan pagination server-side, sehingga
                daftar pembayaran tetap cepat walaupun record terus bertambah.
              </p>
            </div>

            <div className="payments-overview__stats">
              <span className="status-chip">
                <FaList /> Menampilkan {startRecord}-{endRecord}
              </span>
              <span className="status-chip">
                <FaCreditCard /> Total record {totalRows}
              </span>
            </div>
          </div>

          <div className="payments-filter-panel">
            <div className="payments-filter-panel__header">
              <div>
                <span className="page-eyebrow">Filter & export</span>
                <h3>Refine payment records</h3>
              </div>

              <div className="payments-export-group">
                <Button variant="success" onClick={exportExcel}>
                  <FaFileExcel /> Export Excel
                </Button>
                <Button variant="danger" onClick={exportPDF}>
                  <FaFilePdf /> Export PDF
                </Button>
              </div>
            </div>

            <div className="payments-quick-range">
              <button
                type="button"
                className={`payments-quick-range__chip ${quickRange === 'today' ? 'is-active' : ''}`}
                onClick={() => handleQuickRange('today')}
              >
                Hari ini
              </button>
              <button
                type="button"
                className={`payments-quick-range__chip ${quickRange === 'this-week' ? 'is-active' : ''}`}
                onClick={() => handleQuickRange('this-week')}
              >
                Minggu ini
              </button>
              <button
                type="button"
                className={`payments-quick-range__chip ${quickRange === 'this-month' ? 'is-active' : ''}`}
                onClick={() => handleQuickRange('this-month')}
              >
                Bulan ini
              </button>
              <button
                type="button"
                className={`payments-quick-range__chip ${quickRange === 'all' ? 'is-active' : ''}`}
                onClick={() => {
                  setQuickRange('all');
                  resetFilters();
                }}
              >
                Semua
              </button>
            </div>

            <div className="toolbar-grid payments-filter-grid">
              <Form.Group className="field-stack span-4">
                <Form.Label>Search</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaSearch />
                  </span>
                  <Form.Control
                    type="text"
                    value={searchTerm}
                    placeholder="Cari member, outlet, membership, atau petugas"
                    onChange={event => setSearchTerm(event.target.value)}
                  />
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Payment method</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaCreditCard />
                  </span>
                  <Form.Select value={methodFilter} onChange={event => setMethodFilter(event.target.value)}>
                    <option value="">All Methods</option>
                    <option value="Cash">Cash</option>
                    <option value="Transfer">Transfer</option>
                    <option value="QRIS">QRIS</option>
                  </Form.Select>
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Status</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaClock />
                  </span>
                  <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                    <option value="">Semua status</option>
                    <option value="1">Completed</option>
                    <option value="0">Pending</option>
                    <option value="2">Failed</option>
                    <option value="3">Cancelled</option>
                  </Form.Select>
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-3">
                <Form.Label>Date from</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaCalendarAlt />
                  </span>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={event => {
                      setQuickRange('custom');
                      setDateFrom(event.target.value);
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-3">
                <Form.Label>Date to</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaCalendarAlt />
                  </span>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={event => {
                      setQuickRange('custom');
                      setDateTo(event.target.value);
                    }}
                  />
                </div>
              </Form.Group>

              <div className="payments-filter-actions span-6">
                <Button onClick={applyFilter}>
                  <FaFilter /> Apply
                </Button>
                <Button variant="secondary" onClick={resetFilters}>
                  <FaRedoAlt /> Reset
                </Button>
              </div>
            </div>
          </div>

          <div className="payments-table-head">
            <div>
              <span className="page-eyebrow">Records</span>
              <h3>Membership payment list</h3>
              <p>Status pembayaran dapat diperbarui langsung dari tabel, sementara statistik besar dipantau dari kartu ringkasan di atas.</p>
            </div>
            <span className="soft-stat">
              <FaList /> {startRecord}-{endRecord} dari {totalRows} data
            </span>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={payments}
              shellClassName="payments-data-table-shell"
              tableMinWidth="1400px"
              customStyles={paymentsTableStyles}
              responsive={false}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangePage={page => setCurrentPage(page)}
              onChangeRowsPerPage={(nextRowsPerPage, page) => {
                setRowsPerPage(nextRowsPerPage);
                setCurrentPage(page);
              }}
              progressPending={loading}
              highlightOnHover
              pointerOnHover
              emptyTitle="No payment data found"
              emptyDescription="Belum ada transaksi membership yang cocok dengan filter aktif."
            />
          </div>
        </div>
      </Container>
    </>
  );
}

export default MembershipPayments;
